import { t } from '@lingui/macro';
import {
  type ApiClient,
  ExperienceCreatedFrom,
  QuarkExperienceTypes,
  QuarkExperiences,
  RefDesignations,
  RefIndustries,
} from '@prismo-io/schemas';
import { z } from 'zod';

const DesignationSchema = z.object({
  name: RefDesignations.shape.name.nullable(),
  code: RefDesignations.shape.code.nullable(),
  referential_id: RefDesignations.shape.referential_id.nullable(),
});

const DomainSchema = z.object({
  code: RefIndustries.shape.code.nullable(),
  referential_id: RefIndustries.shape.referential_id.nullable(),
});

const ParamsSchema = z.object({
  id: z.string().uuid().nullable(),
  customName: z.string().nullable(),
  designation: DesignationSchema,
  type_id: QuarkExperienceTypes.shape.id.nullish(),
  company: z.string().nullable(),
  yearStart: z.string().nullable(),
  yearEnd: z.string().nullable(),
  favorite: z.number().int().nullable(),
  domains: z.array(DomainSchema).nullable(),
  createdFrom: ExperienceCreatedFrom.optional(),
});

export type ParamsSchemaT = z.infer<typeof ParamsSchema>;

const ResultSchema = QuarkExperiences.pick({ id: true });

export type ResultSchemaT = z.infer<typeof ResultSchema>;

export const sendExperience = async (
  client: ApiClient,
  params: ParamsSchemaT
): Promise<ResultSchemaT> => {
  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session) {
    throw new Error(t`Vous devez être connecté.`);
  }

  const userId = session.user.id;

  const validParams = ParamsSchema.parse(params);

  if (validParams.favorite) {
    const { data: experience } = await client
      .from('quark_experiences')
      .select('id')
      .eq('favorite', validParams.favorite)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle()
      .throwOnError();

    if (experience) {
      await client
        .from('quark_experiences')
        .update({
          favorite: null,
        })
        .eq('id', experience.id)
        .throwOnError();
    }
  }

  const { data: experience } = await client
    .from('quark_experiences')
    .upsert({
      id: validParams.id ?? undefined,
      custom_name: validParams.customName,
      designation_code: validParams.designation.code,
      designation_referential_id: validParams.designation.referential_id,
      type_id: validParams.type_id ?? undefined,
      company: validParams.company,
      start_at: validParams.yearStart,
      end_at: validParams.yearEnd,
      favorite: validParams.favorite,
      created_from: validParams.createdFrom,
    })
    .select('id')
    .single()
    .throwOnError();

  if (!experience) {
    throw new Error('Erreur lors de la création.');
  }

  await client
    .from('quark_experiences_industries')
    .delete()
    .eq('experience_id', experience.id)
    .throwOnError();

  const { domains } = validParams;

  if (domains) {
    try {
      await client
        .from('quark_experiences_industries')
        .insert(
          domains.map((domain) => {
            return {
              experience_id: experience.id,
              industry_code: domain.code,
              industry_referential_id: domain.referential_id,
            };
          })
        )
        .throwOnError();
    } catch (error) {
      await client.from('quark_experiences').delete().eq('id', experience.id);

      throw error;
    }
  }

  return ResultSchema.parse(experience);
};
